import Notifications from 'react-notification-system-redux';
import { pagePathChanged } from '../../../general/modules/router/actions';
import { loadUser } from '../../../general/sagas/loadUser';
import { TSagaReturn } from '../../../general/scenarioFactory';
import { hubRoutes } from '../../../general/urls';
import { ListenerApiLight, _fork, _select, _take, lm } from '../../listenerMiddleware';
import { logger } from '../../logger';
import { Page } from '../../models/page';
import { apiRequestBreadcrumbs } from '../breadcrumbs/listeners';
import * as appActions from './actions';
import { getPage, getShareID, getUser } from './selectors';
import { userExitsAnonymousPages } from '../../awaitUserExitsAnonymousPages';
import { api } from '../../../general/apiScenarios';
import { misleadSingleton } from './misleadSingleton';
import {
  SLOW_NETWORK_MESSAGE,
  SLOW_NETWORK_WARNING_DISMISS_S,
  watchNetworkSpeed,
} from '../../../general/slowNetwork';

export function appListeners() {
  init();
  watchPagePathChanged();
  preloadEncryptedSessionID();
}

const showSlowNetworkWarning = (dispatch: any) => {
  dispatch(
    Notifications.warning({
      title: 'Slow Network Speed',
      message: SLOW_NETWORK_MESSAGE,
      autoDismiss: SLOW_NETWORK_WARNING_DISMISS_S,
      position: 'tr',
    }),
  );
};

// Замер и перепроверка живут в general/slowNetwork.ts — их разделяет Asset Lab;
// здесь только показ результата средствами Creative Hub (MYUA-16416).
function checkNetworkSpeed(dispatch: any) {
  void watchNetworkSpeed({
    onSlowDetected: () => {
      showSlowNetworkWarning(dispatch);
      dispatch(appActions.slowNetworkChanged({ slow: true }));
    },
    onRestored: () => dispatch(appActions.slowNetworkChanged({ slow: false })),
  });
}

function init() {
  lm.takeOnce(appActions.initStarted, async (_, lapi) => {
    const { getState, dispatch } = lapi;

    checkNetworkSpeed(dispatch);

    // Юзер, как правило, уже загружен и положен в стор в index.tsx (bootstrap) — до
    // initStarted(). Повторно грузим только если его нет (напр. анонимная шаренная
    // страница, где gate стартует приложение без пользователя).
    let user = getUser(getState());
    if (!user) {
      await loadUser(lapi);
      user = getUser(getState());
    }

    if (user) {
      logger.lapi(lapi, { action: 'Service open' });

      const misleadsResult = await api.bl.loadMisleadLevels();
      if (misleadsResult.success) {
        misleadSingleton.setAll(misleadsResult.data.response);
      } else {
        dispatch(Notifications.error({ title: 'Failed to load mislead levels' }));
      }
    }

    dispatch(appActions.initListeners());
    dispatch(appActions.initialized());
  });

  lm.takeOnce(appActions.initStarted, async (_, { dispatch }) => {
    const result = await api.crhub.settings.load();
    if (result.success) {
      dispatch(appActions.settingsLoaded({ settings: result.data.response }));
    } else {
      dispatch(Notifications.error({ title: 'Settings have not been loaded' }));
    }
  });
}

function preloadEncryptedSessionID() {
  lm.takeOnce(appActions.initListeners, async (_, lapi) => {
    await userExitsAnonymousPages(lapi);
  });
}

function watchPagePathChanged() {
  lm.takeLatest(pagePathChanged, async function (action, lapi) {
    const { page, match, hubSearchPageParams } = action.payload;

    if (page === Page.SEARCH && hubSearchPageParams) {
      let query;

      try {
        query = decodeURIComponent(hubSearchPageParams.query);
      } catch (error) {
        query = '';
      }

      if (query === '' && hubSearchPageParams.tags.length === 0) {
        window.location.hash = hubRoutes.root;
        return;
      }
    }

    if (page === Page.FOLDER) {
      let { nodeId } = match.params;
      if (!nodeId) {
        const rootNodeResult = await loadRootNode(lapi);
        lapi.signal.throwIfAborted();
        if (rootNodeResult.success) {
          nodeId = rootNodeResult.data;
        }
      }
      if (nodeId !== undefined) {
        lapi.dispatch(appActions.nodeIDChanged({ nodeID: parseInt(nodeId) }));
      }
    }

    if (page === Page.SHARED_FOLDER) {
      const { shareId } = match.params;
      lapi.dispatch(appActions.shareIdChanged({ shareId }));

      let { nodeId } = match.params;
      if (!nodeId) {
        const rootNodeResult = await loadRootNode(lapi);
        lapi.signal.throwIfAborted();
        if (rootNodeResult.success) {
          nodeId = rootNodeResult.data;
        }
      }
      if (nodeId !== undefined) {
        lapi.dispatch(appActions.nodeIDChanged({ nodeID: parseInt(nodeId) }));
      }
    }

    if (page === Page.SHARED_LINKS || page === Page.FAVORITES) {
      lapi.dispatch(appActions.nodeIdErased());
    }
  });
}

async function loadRootNode(lapi: ListenerApiLight): Promise<TSagaReturn<number>> {
  lapi.dispatch(appActions.loadRootNodeStarted());
  const getPageResult = _select(lapi, getPage);
  const getShareIdResult = _select(lapi, getShareID);

  const result = await apiRequestBreadcrumbs(undefined, getPageResult, getShareIdResult);
  lapi.signal.throwIfAborted();

  if (result.success) {
    const nodeId = result.data.response.nodes[0].id;
    lapi.dispatch(appActions.loadRootNodeSuccess({ result, nodeId }));
    return {
      success: true,
      args: {},
      data: nodeId,
    };
  } else {
    lapi.dispatch(appActions.loadRootNodeError({ error: result.reason }));
    lapi.dispatch(
      Notifications.error({
        title: 'Server request error',
        message:
          result.errorData?.response.data ??
          'Root folder has not been received. Contact your administrator or support chat.',
      }),
    );
    return {
      success: false,
      args: {},
    } as TSagaReturn;
  }
}
